import { Fragment } from 'react';
import type { FormEvent, ReactNode } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';

/**
 * Atoms replacement for the bricks-next `FormLayout` compound component
 * (`@opendoor/bricks-next` had no Atoms drop-in). Reproduces the page chrome —
 * scrollable body, top nav, two-column content, bottom nav, legal copy — with
 * Atoms primitives and semantic Tailwind, preserving the original Opendoor form
 * spacing/typography tokens. Used by the signup/address onboarding pages.
 *
 * Layout spacing mirrors the bricks `formLayout` tokens: bricks space tokens are
 * half-scale (`$Nx` = 2·N px → Tailwind `N/2`) and size tokens are literal px
 * (`$Nx` = N px → Tailwind `N/4`).
 */

type FormLayoutRootProps = {
  /** id for the underlying `<form>` element. */
  formId: string;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
  className?: string;
};

const FormLayoutRoot = ({ formId, onSubmit, children, className }: FormLayoutRootProps) => (
  // eslint-disable-next-line @opendoor/no-html-elements -- Atoms has no form-element primitive; a native <form> preserves native submit (Enter key + type="submit") semantics
  <form
    id={formId}
    onSubmit={onSubmit}
    className={cn('relative flex min-h-dvh w-full flex-col bg-od-bg-primary', className)}
  >
    {children}
  </form>
);

/** Scrollable form body. Wraps everything except the BottomNav. */
const ScrollContainer = ({ children }: { children: ReactNode }) => (
  <Col className="grow bg-od-bg-primary">{children}</Col>
);

type TopNavTextButtonProps = {
  label: string;
  analyticsName: string;
  /** Renders an anchor when provided. */
  href?: string;
  /** Renders a button when `href` is absent. */
  onPress?: () => void;
};

type TopNavProps =
  | { navLabel?: string; textButtonProps?: TopNavTextButtonProps; children?: never }
  | { children: ReactNode; navLabel?: never; textButtonProps?: never };

// Renders the nav action as a text-styled Button (so it carries click analytics
// and an accessible name) for both the navigation (href) and action (onPress) cases.
const TOP_NAV_BUTTON_CLASS =
  'h-auto border-0 bg-transparent p-0 text-label-md !text-od-content-accent-brand not-disabled:hover:bg-transparent not-disabled:hover:!text-od-content-hover-accent-brand';

const NAV_PADDING = 'px-6 py-4.5 md:px-8 md:py-5 lg:px-14';

const TopNav = (props: TopNavProps) => {
  if ('children' in props && props.children != null) {
    return <Col className={NAV_PADDING}>{props.children}</Col>;
  }

  const navLabel = 'navLabel' in props ? props.navLabel : undefined;
  const textButtonProps = 'textButtonProps' in props ? props.textButtonProps : undefined;

  let actionButton: ReactNode = null;
  if (textButtonProps?.label) {
    actionButton =
      textButtonProps.href != null ? (
        <Button
          aria-label={textButtonProps.label}
          href={textButtonProps.href}
          variant="primary"
          size="sm"
          analyticsName={textButtonProps.analyticsName}
          className={TOP_NAV_BUTTON_CLASS}
        >
          {textButtonProps.label}
        </Button>
      ) : (
        <Button
          aria-label={textButtonProps.label}
          onClick={textButtonProps.onPress}
          variant="primary"
          size="sm"
          analyticsName={textButtonProps.analyticsName}
          className={TOP_NAV_BUTTON_CLASS}
        >
          {textButtonProps.label}
        </Button>
      );
  }

  return (
    <Col className={NAV_PADDING}>
      <Row className="w-full items-center justify-between">
        <Col>
          {navLabel ? (
            <Text as="span" className="text-label-md text-od-content-primary">
              {navLabel}
            </Text>
          ) : null}
        </Col>
        <Col>{actionButton}</Col>
      </Row>
    </Col>
  );
};

/**
 * Title + questions container. Stacks on mobile and becomes a two-column row at
 * `md`, matching the bricks content container. Pass `className` for per-page
 * tweaks (e.g. extra bottom padding to clear an absolutely-positioned footer).
 */
const FormContent = ({ children, className }: { children: ReactNode; className?: string }) => (
  <Box
    className={cn(
      'grow flex-col gap-10 px-6 pt-10 md:flex-row md:px-8 md:pt-12 lg:gap-14 lg:px-14 lg:pt-14',
      className,
    )}
  >
    {children}
  </Box>
);

const Title = ({ title, subhead }: { title: string; subhead?: ReactNode }) => (
  <Col className="gap-4 md:basis-1/2 md:shrink md:gap-6">
    <Text as="h1" className="text-header-sm text-od-content-primary md:text-header-lg">
      {title}
    </Text>
    {typeof subhead === 'string' ? (
      <Text as="span" className="text-body-md text-od-content-tertiary md:text-body-lg">
        {subhead}
      </Text>
    ) : (
      subhead
    )}
  </Col>
);

const QuestionContainer = ({ children }: { children: ReactNode }) => (
  <Col className="gap-12 md:basis-1/2 md:shrink md:gap-16 lg:gap-20">{children}</Col>
);

type LegalCopyLink = {
  text: string;
  href: string;
  analyticsName: string;
};

const LegalCopy = ({ links = [], className }: { links?: LegalCopyLink[]; className?: string }) => (
  <Text as="small" className={cn('text-body-xs text-od-content-primary', className)}>
    &copy; {new Date().getFullYear()} Opendoor | Equal Housing Opportunity
    {links.length > 0 ? ' | ' : ''}
    {links.map((link, idx) => (
      <Fragment key={link.text}>
        <Link
          href={link.href}
          analyticsName={link.analyticsName}
          className="text-body-xs text-od-content-primary"
        >
          {link.text}
        </Link>
        {idx === links.length - 1 ? '' : ' · '}
      </Fragment>
    ))}
  </Text>
);

type BottomNavButtonProps = {
  label: string;
  analyticsName: string;
  loading?: boolean;
  disabled?: boolean;
  /** Only used for the secondary button; the submit button uses native form submit. */
  onPress?: () => void;
};

type BottomNavProps = {
  submitButtonProps?: BottomNavButtonProps;
  secondaryButtonProps?: BottomNavButtonProps;
  progressBarProps?: { completed: number };
};

// Bottom nav buttons are small on mobile, large from `md` up (bricks size
// small → large). Atoms `size` isn't responsive, so start at `lg` and shrink at
// `max-md`.
const BOTTOM_NAV_BUTTON_CLASS = 'max-md:h-8 max-md:rounded-xl max-md:px-4 max-md:text-label-md';

const BottomNav = ({
  submitButtonProps,
  secondaryButtonProps,
  progressBarProps,
}: BottomNavProps) => {
  const completed = progressBarProps
    ? Math.min(1, Math.max(0, progressBarProps.completed))
    : undefined;

  return (
    <Col className="pointer-events-none fixed inset-x-0 bottom-0 z-10">
      <Col className="pointer-events-none h-22 bg-gradient-to-b from-transparent to-od-bg-primary" />
      {completed != null ? (
        <Col
          role="progressbar"
          aria-valuenow={Math.round(completed * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          className="h-1 w-full bg-od-border-primary"
        >
          {/* eslint-disable-next-line @opendoor/no-html-elements -- a computed width percentage has no Tailwind equivalent and Box strips inline style */}
          <div
            className="h-full bg-od-bg-accent-brand transition-[width] duration-500 ease-in-out"
            style={{ width: `${completed * 100}%` }}
          />
        </Col>
      ) : null}
      <Row className="pointer-events-auto items-center justify-between bg-od-bg-primary px-6 py-2.5 md:px-8 lg:px-14">
        <Col />
        <Row className="gap-3">
          {secondaryButtonProps ? (
            <Button
              aria-label={secondaryButtonProps.label}
              variant="secondary"
              size="lg"
              className={BOTTOM_NAV_BUTTON_CLASS}
              onClick={secondaryButtonProps.onPress}
              loading={secondaryButtonProps.loading}
              disabled={secondaryButtonProps.disabled}
              analyticsName={secondaryButtonProps.analyticsName}
            >
              {secondaryButtonProps.label}
            </Button>
          ) : null}
          {submitButtonProps ? (
            <Button
              type="submit"
              aria-label={submitButtonProps.label}
              variant="primary"
              size="lg"
              className={BOTTOM_NAV_BUTTON_CLASS}
              loading={submitButtonProps.loading}
              disabled={submitButtonProps.disabled}
              analyticsName={submitButtonProps.analyticsName}
            >
              {submitButtonProps.label}
            </Button>
          ) : null}
        </Row>
      </Row>
    </Col>
  );
};

/**
 * Compound form-page layout. Usage mirrors the bricks-next original:
 * ```tsx
 * <FormLayout formId="my-form" onSubmit={handleSubmit}>
 *   <FormLayout.ScrollContainer>
 *     <FormLayout.TopNav navLabel="…" textButtonProps={{ … }} />
 *     <FormLayout.FormContent>
 *       <FormLayout.Title title="…" subhead="…" />
 *       <FormLayout.QuestionContainer>{fields}</FormLayout.QuestionContainer>
 *     </FormLayout.FormContent>
 *   </FormLayout.ScrollContainer>
 *   <FormLayout.BottomNav submitButtonProps={{ … }} />
 * </FormLayout>
 * ```
 */
export const FormLayout = Object.assign(FormLayoutRoot, {
  ScrollContainer,
  TopNav,
  FormContent,
  Title,
  QuestionContainer,
  LegalCopy,
  BottomNav,
});
